import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { BaseKendoList } from '@common/classes/base-kendo-list';
import { ChatFilter } from '@common/models/Chat/ChatFilter';
import { PagingAndOrderingModel } from '@common/models/common/PagingAndOrdering.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { ChatService } from '@services/chat.service';
import { BaseKendoListService } from 'app/singleton-services/base-kendo-list.service';
import { ChatCounterService } from 'app/singleton-services/chat-counter.service';

export enum ChatMode {
    General = 'general',
    VesselVisit = 'vesselVisit',
    ServiceRequest = 'serviceRequest',
    StorageDocument = 'storageDocument',
    AmendmentDocument = 'amendmentDocument',
    Sent = 'sent'
}

@Component({
    selector: 'app-tab-chat',
    templateUrl: './tab-chat.component.html',
    styleUrls: ['./tab-chat.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TabChatComponent extends BaseKendoList implements OnInit {
    @Input() vesselVisitId: number | null = null;
    @Input() serviceRequestId: number | null = null;
    @Input() storageDocumentId: number | null = null;
    @Input() amendmentDocumentId: number | null = null;
    @Input() mode: string = ChatMode.General;

    filter: ChatFilter = new ChatFilter();

    // Make ChatMode available in template
    ChatMode = ChatMode;
    rawData: [] = [];
    dialogAddNewChatNoteOpen = false;
    dialogExpandChatNote = false;
    openedNoteId = null;
    isNew: boolean;
    isBusy = false;
    canAddNewNote: boolean = false;
    differentChatNoteView: boolean = false;

    constructor(
        baseKendoListService: BaseKendoListService,
        private service: ChatService,
        private chatCounterService: ChatCounterService
    ) {
        super(baseKendoListService);
    }

    public getDefaultFilter(): ChatFilter {
        const pageAndFilterObj = new PagingAndOrderingModel(this.pageObj.skip, this.pageObj.take);
        const filter = new ChatFilter(pageAndFilterObj);

        return filter;
    }

    public listQuery(filter: ChatFilter) {
        if (this.differentChatNoteView) {
            if (this.mode === ChatMode.Sent) {
                // For sent messages, get all different chat notes and filter by current user
                return this.service.getDifferentChatNotes({
                    ...filter,
                    sentByMe: true
                });
            } else {
                return this.service.getDifferentChatNotes({
                    ...filter,
                    forVesselVisit: this.mode === ChatMode.VesselVisit,
                    forServiceRequest: this.mode === ChatMode.ServiceRequest,
                    forStorageDocument: this.mode === ChatMode.StorageDocument,
                    forAmendmentDocument: this.mode === ChatMode.AmendmentDocument
                });
            }
        }

        return this.service.getChatNotes({
            ...filter,
            vesselVisitId: this.vesselVisitId,
            serviceRequestId: this.serviceRequestId,
            storageDocumentId: this.storageDocumentId,
            amendmentDocumentId: this.amendmentDocumentId
        });
    }

    ngOnInit(): void {
        this.differentChatNoteView =
            (this.mode === ChatMode.VesselVisit && !this.vesselVisitId) ||
            (this.mode === ChatMode.ServiceRequest && !this.serviceRequestId) ||
            (this.mode === ChatMode.StorageDocument && !this.storageDocumentId) ||
            (this.mode === ChatMode.AmendmentDocument && !this.amendmentDocumentId) ||
            this.mode === ChatMode.Sent;

        super.ngOnInit();
    }

    public openDialogAddNewChatNote() {
        this.dialogAddNewChatNoteOpen = true;
    }

    public closeDialogAddNewChatNote() {
        this.dialogAddNewChatNoteOpen = false;
    }

    public closeDialogAddNewChatNoteEvent($event) {
        if ($event) {
            this.searchForm();
        }
        this.closeDialogAddNewChatNote();
    }

    public openDialogExpandChatNote(event: any) {
        const { id, isNew } = event.dataItem;
        this.openedNoteId = id;
        this.isNew = isNew;
        this.dialogExpandChatNote = true;
    }

    public closeDialogExpandChatNote() {
        this.openedNoteId = null;
        this.isNew = false;
        this.dialogExpandChatNote = false;
        this.chatCounterService.refreshCounts();
        this.searchForm();
    }

    public closeDialogExpandChatNoteEvent($event) {
        if ($event) {
            this.searchForm();
        }
        this.closeDialogExpandChatNote();
    }

    rowCallback = (context: RowClassArgs) => {
        return {
            'unread-message': context.dataItem?.isNew && context.dataItem?.forUser?.id === this.user?.id,
            'clickable-row': true
        };
    };

    public getChatTypeBadgeClass(chatTypeCode: string): string {
        if (!chatTypeCode) return 'general';

        const lowerCode = chatTypeCode.toLowerCase();

        if (lowerCode.includes('vessel') || lowerCode.includes('visit')) {
            return 'vessel-visit';
        } else if (lowerCode.includes('service') || lowerCode.includes('request')) {
            return 'service-request';
        } else if (lowerCode.includes('storage') || lowerCode.includes('document')) {
            return 'storage-document';
        } else if (lowerCode.includes('amendment')) {
            return 'amendment-document';
        } else {
            return 'general';
        }
    }

    public getChatTypeIcon(chatTypeCode: string): { icon: IconProp; tooltip: string } {
        if (!chatTypeCode) {
            return { icon: ['fas', 'comments'], tooltip: 'General Chat' };
        }

        const lowerCode = chatTypeCode.toLowerCase();

        // For specific user messages
        if (lowerCode.includes('for_user') || lowerCode.includes('user')) {
            return { icon: ['fas', 'user'], tooltip: 'User Message' };
        }
        // For organization messages
        else if (lowerCode.includes('for_org') || lowerCode.includes('organization') || lowerCode.includes('org')) {
            return { icon: ['fas', 'building'], tooltip: 'Organization Message' };
        }
        // Internal system messages
        else if (lowerCode.includes('internal') || lowerCode.includes('system')) {
            return { icon: ['fas', 'cog'], tooltip: 'Internal Message' };
        }
        // Public/All messages
        else if (lowerCode.includes('all') || lowerCode.includes('public') || lowerCode.includes('general')) {
            return { icon: ['fas', 'globe'], tooltip: 'Public Message' };
        }
        // Vessel/Visit related
        else if (lowerCode.includes('vessel') || lowerCode.includes('visit')) {
            return { icon: ['fas', 'ship'], tooltip: 'Vessel Visit Message' };
        }
        // Service request related
        else if (lowerCode.includes('service') || lowerCode.includes('request')) {
            return { icon: ['fas', 'hands-helping'], tooltip: 'Service Request Message' };
        }
        // Storage/Document related
        else if (lowerCode.includes('storage') || lowerCode.includes('document')) {
            return { icon: ['fas', 'file-alt'], tooltip: 'Document Message' };
        }
        // Amendment related
        else if (lowerCode.includes('amendment')) {
            return { icon: ['fas', 'edit'], tooltip: 'Amendment Message' };
        }
        // Default fallback
        else {
            return { icon: ['fas', 'comments'], tooltip: 'Chat Message' };
        }
    }

    public getUserInitials(displayName: string): string {
        if (!displayName) return '?';

        const names = displayName.trim().split(' ');
        if (names.length === 1) {
            return names[0].substring(0, 2).toUpperCase();
        } else {
            return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        }
    }

    public truncateText(text: string, maxLength: number): string {
        if (!text) return '';

        if (text.length <= maxLength) {
            return text;
        } else {
            return text.substring(0, maxLength) + '...';
        }
    }

    public getRecipientInfo(row: any): { icon: IconProp; text: string; tooltip: string; showTypeInfo: boolean } {
        // Determine recipient based on chat type and target
        const chatType = row.chatNoteType?.localizedCode?.toLowerCase() || '';

        // Check if it's for a specific user
        if (row.forUser) {
            return {
                icon: ['fas', 'user'],
                text: `${row.forUser.firstName} ${row.forUser.lastName}`,
                tooltip: `Message for specific user: ${row.forUser.firstName} ${row.forUser.lastName}`,
                showTypeInfo: this.shouldShowTypeInfo(chatType)
            };
        }

        // Check if it's for a specific organization
        if (row.forOrganization) {
            return {
                icon: ['fas', 'building'],
                text: `${row.forOrganization.code} - ${row.forOrganization.name}`,
                tooltip: `Message for organization: ${row.forOrganization.name}`,
                showTypeInfo: this.shouldShowTypeInfo(chatType)
            };
        }

        // Check chat type patterns for general audience
        if (chatType.includes('all') || chatType.includes('public') || chatType.includes('general')) {
            return {
                icon: ['fas', 'globe'],
                text: 'Everyone',
                tooltip: 'Public message for all users',
                showTypeInfo: this.shouldShowTypeInfo(chatType)
            };
        }

        if (chatType.includes('internal') || chatType.includes('system')) {
            return {
                icon: ['fas', 'cog'],
                text: 'Internal',
                tooltip: 'Internal system message',
                showTypeInfo: true // Always show type info for internal messages
            };
        }

        // Default fallback
        return {
            icon: ['fas', 'comments'],
            text: 'General',
            tooltip: 'General chat message',
            showTypeInfo: this.shouldShowTypeInfo(chatType)
        };
    }

    private shouldShowTypeInfo(chatType: string): boolean {
        // Show type information for internal, system, amendment, or special message types
        const lowerType = chatType?.toLowerCase() || '';
        return (
            lowerType.includes('internal') ||
            lowerType.includes('system') ||
            lowerType.includes('amendment') ||
            lowerType.includes('vessel') ||
            lowerType.includes('service') ||
            lowerType.includes('storage') ||
            lowerType.includes('document')
        );
    }
}
